import type { DeliveryLockSelection } from '@/services/deliveryLockService';
import type { LastSentInfo } from '@/services/rateLockConfirmationService';

interface DeliveryLockBannerProps {
  selection: DeliveryLockSelection;
  /** Display label for the active lock version. */
  displayLabel?: string | null;
  /** Display capture timestamp for the active lock version. */
  displayCapturedAt?: string | null;
  /**
   * Wired by ProductPricingWorkspace. When omitted, the send affordance is hidden
   * (e.g., in Banker Recon's reuse of this banner).
   */
  onSendConfirmation?: () => void;
  /**
   * True when the workspace's pricing draft has unsaved changes. The banner
   * disables the send button locally with a tooltip — server-side gates run
   * inside the modal after preview fetch.
   */
  pricingDirty?: boolean;
  /**
   * Most-recent send for the active selection. Renders inline as
   * "Sent {Pacific timestamp} PT" with "by {email}" appended only when
   * sentByEmail is non-null. Also drives the Send button label —
   * "Resend email with LO pricing" when set, "Send email with LO pricing"
   * when null.
   */
  lastSent?: LastSentInfo | null;
  /**
   * Set when a newer lock version exists. Click does NOT switch the active
   * selection; it only navigates the LockHistoryPicker to the newest version
   * for review (operator must explicitly Set Active).
   */
  newerLockVersionAvailable?: boolean;
  onReviewLatest?: () => void;
}

const PACIFIC_TIMESTAMP_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  dateStyle: 'medium',
  timeStyle: 'short'
});

function formatLastSentLine(info: LastSentInfo | null | undefined): string | null {
  if (!info) {
    return null;
  }
  let when = info.sentAt;
  try {
    when = PACIFIC_TIMESTAMP_FORMATTER.format(new Date(info.sentAt));
  } catch {
    /* fall through with raw ISO */
  }
  const base = `Sent ${when} PT`;
  return info.sentByEmail ? `${base} by ${info.sentByEmail}` : base;
}

function formatCapturedAt(iso: string | null): string | null {
  if (!iso) {
    return null;
  }
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  } catch {
    return null;
  }
}

export function DeliveryLockBanner({
  selection,
  displayLabel,
  displayCapturedAt,
  onSendConfirmation,
  pricingDirty = false,
  lastSent = null,
  newerLockVersionAvailable = false,
  onReviewLatest
}: DeliveryLockBannerProps) {
  const captured = formatCapturedAt(
    displayCapturedAt ?? selection.lockVersionCapturedAt ?? selection.snapshotCapturedAt
  );
  const label = displayLabel ?? selection.lockVersionLabel ?? selection.snapshotLabel ?? 'Selected delivery lock';
  const lastSentLine = formatLastSentLine(lastSent);
  const sendLabel = lastSent ? 'Resend email with LO pricing' : 'Send email with LO pricing';

  return (
    <div className="sn-banner delivery-lock" role="status" aria-live="polite">
      <svg
        className="sn-banner-icon"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden
      >
        <path d="M20 6 9 17l-5-5" />
      </svg>
      <div className="sn-banner-body">
        <div className="sn-banner-eyebrow">Delivery lock selected</div>
        <div className="sn-banner-text">
          <b>{label}</b>
          {captured && <> · captured {captured}</>} is driving the lock fields for pricing and recon. Local pricing edits (discount, BPC, lender credit, LO comp reduction) remain live.
        </div>
        <div
          className="sn-banner-meta"
          data-testid="delivery-lock-email-source-notice"
          style={{ marginTop: 4, fontSize: 12, color: '#4b5563' }}
        >
          Confirmation email uses this lock version plus current live pricing edits.
        </div>
        {newerLockVersionAvailable && onReviewLatest && (
          <div
            className="sn-banner-meta sn-banner-newer"
            style={{ marginTop: 4, fontSize: 12, color: '#b45309' }}
          >
            <span>Newer lock version available · </span>
            <button
              type="button"
              onClick={onReviewLatest}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#b45309',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
                font: 'inherit'
              }}
            >
              Review latest
            </button>
          </div>
        )}
        {lastSentLine && (
          <div className="sn-banner-meta" style={{ marginTop: 4, fontSize: 12, color: '#4b5563' }}>
            {lastSentLine}
          </div>
        )}
      </div>
      {onSendConfirmation && (
        <button
          type="button"
          className="sn-banner-action"
          onClick={onSendConfirmation}
          disabled={pricingDirty}
          title={pricingDirty ? 'Save pricing changes before sending' : undefined}
        >
          {sendLabel}
        </button>
      )}
    </div>
  );
}
